import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Instagram, ArrowUpRight } from 'lucide-react';
import { PaymentMethods } from '@/components/PaymentMethods';

const navLinks = [
  { to: '/collezione',   label: 'Collezione' },
  { to: '/chi-siamo',    label: 'Chi Siamo' },
  { to: '/appuntamento', label: 'Prenota Appuntamento' },
  { to: '/contatti',     label: 'Contatti' },
];

const currentYear = new Date().getFullYear();

export default function Footer() {
  return (
    <footer className="bg-ink text-ivory relative" role="contentinfo">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-bordeaux to-transparent" />

      <div className="max-w-container mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 lg:gap-10">
          {/* Brand */}
          <div className="lg:col-span-4">
            <Link to="/" className="inline-block mb-8 group" data-testid="footer-logo">
              <img
                src="/logo-white.png"
                alt="Ciro Gallotti — L'Arte del Gioiello"
                className="h-24 w-auto object-contain"
              />
              <p className="text-eyebrow text-ivory mt-3">L'Arte del Gioiello</p>
            </Link>
            <p className="text-ivory/50 text-sm leading-relaxed mb-8 max-w-sm">
              Dal 1968, creiamo gioielli che raccontano la bellezza e la tradizione di Napoli.
            </p>

            <div className="flex items-center gap-3">
              <a
                href="https://www.instagram.com/cirogallotti_jewerlydesign/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram Ciro Gallotti"
                className="w-11 h-11 rounded-full border border-ivory/15 flex items-center justify-center hover:border-bordeaux hover:bg-bordeaux transition-colors duration-hover group"
                data-testid="social-instagram"
              >
                <Instagram className="w-4 h-4 text-ivory/70 group-hover:text-ivory" />
              </a>
            </div>
          </div>

          {/* Navigation */}
          <div className="lg:col-span-2">
            <h4 className="text-eyebrow text-ivory mb-7">Navigazione</h4>
            <nav className="flex flex-col gap-4" aria-label="Footer">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="text-sm text-ivory/55 hover:text-ivory transition-colors duration-hover flex items-center gap-2 group w-fit"
                  data-testid={`footer-${link.to.slice(1)}`}
                >
                  {link.label}
                  <ArrowUpRight className="w-3 h-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-hover" />
                </Link>
              ))}
            </nav>
          </div>

          {/* Contact */}
          <div className="lg:col-span-3">
            <h4 className="text-eyebrow text-ivory mb-7">Contatti</h4>
            <ul className="flex flex-col gap-5 text-sm">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-ivory mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                <span className="text-ivory/55">
                  Via Chiaia 138<br />
                  80121 Napoli, Italia
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-ivory flex-shrink-0" strokeWidth={1.5} />
                <a href="tel:+393336450082" className="text-ivory/55 hover:text-ivory transition-colors duration-hover">
                  +39 333 645 0082
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-ivory flex-shrink-0" strokeWidth={1.5} />
                <a href="mailto:cirogallottijewelry@libero.it" className="text-ivory/55 hover:text-ivory transition-colors duration-hover">
                  cirogallottijewelry@libero.it
                </a>
              </li>
            </ul>
          </div>

          {/* Hours */}
          <div className="lg:col-span-3">
            <h4 className="text-eyebrow text-ivory mb-7">Orari di Apertura</h4>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-ivory/55">Lunedì – Sabato</dt>
                <dd className="text-ivory tabular-nums">10:00 – 19:00</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-ivory/55">Domenica</dt>
                <dd className="text-ivory/30">Chiuso</dd>
              </div>
            </dl>

            <Link
              to="/appuntamento"
              data-testid="footer-appointment-box"
              className="group mt-8 p-5 border border-ivory/20 hover:border-ivory/50 transition-colors duration-hover flex items-center justify-between gap-3"
            >
              <div>
                <p className="text-eyebrow text-ivory mb-1">Prenota un Appuntamento</p>
                <p className="text-sm text-ivory/60">Nel nostro showroom a Napoli</p>
              </div>
              <ArrowUpRight className="w-5 h-5 text-ivory/50 group-hover:text-ivory transition-colors duration-hover flex-shrink-0" />
            </Link>
          </div>
        </div>

        {/* Bottom */}
        <div className="hairline-t border-ivory/10 mt-16 pt-8 space-y-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-eyebrow text-ivory/40">Metodi di pagamento accettati</p>
            <PaymentMethods />
          </div>

          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-eyebrow text-ivory/30 text-center md:text-left">
              © {currentYear} Ciro Gallotti. Tutti i diritti riservati. P.IVA 11025931210
            </p>
            <div className="flex items-center gap-8 text-eyebrow text-ivory/30">
              <Link to="/privacy" className="hover:text-ivory/60 transition-colors duration-hover">
                Privacy Policy
              </Link>
              <Link to="/termini" className="hover:text-ivory/60 transition-colors duration-hover">
                Termini e Condizioni
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
